/**
 * ApplyProgress — the post-approve "what's happening" checklist.
 *
 * After the writer approves a text-edit proposal (edit-highlight or
 * full-section ad-hoc), the apply pipeline runs for 15–30s — dominated
 * by the client-side Office.js Word render — before the "approved &
 * inserted" message lands. This card replaces the ProposalCard in the
 * same tail slot during that window and narrates the known stages
 * (save to record / apply in Word) so the wait is legible.
 *
 * Pure presentation: the owning approve state machine
 * (useHighlightEditFlow / useAdhocProposalFlow) advances the step model
 * across its awaits and passes the projected steps in. Visual language
 * mirrors HomeLoadProgress's done/active/pending markers.
 *
 * SRP: no fetches, no Office.js, no state — every branch renders from
 * plain props.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, spacing, statusColors } from "../tokens";
import AsthraBubbleSurface from "./AsthraBubbleSurface";
import { formatTime } from "./MessageBubble";

export type ApplyStepState = "done" | "active" | "pending";

export interface ApplyStep {
  /** Stable key for React + tests. */
  id: string;
  /** Human-readable label ("Applying in the Word document…"). */
  label: string;
  state: ApplyStepState;
}

interface ApplyProgressProps {
  /** Ordered stages with their current state. */
  steps: ApplyStep[];
  /** Chip text next to "Asthra · HH:MM" (e.g. "Edit highlight"). */
  chipText: string;
  /** One-line mono header naming the target. */
  header: string;
  ariaLabel: string;
}

const useStyles = makeStyles({
  outer: {
    alignSelf: "flex-start",
    maxWidth: "95%",
  },
  bubble: {
    padding: spacing.bubblePadding,
    fontSize: "12.5px",
    wordBreak: "break-word",
  },
  intentChip: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: brandColors.mid,
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.glow}`,
    borderRadius: "8px",
    padding: "0px 6px",
    lineHeight: "13px",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  },
  header: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink3,
    marginBottom: "6px",
    letterSpacing: "0.2px",
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "3px 0",
    fontFamily: fonts.body,
    fontSize: "11.5px",
  },
  stepMarker: {
    flexShrink: 0,
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    border: `1px solid ${inkColors.ink5}`,
    background: "transparent",
  },
  stepMarkerDone: {
    border: "none",
    background: statusColors.cool,
  },
  stepMarkerActive: {
    border: "none",
    background: brandColors.mid,
    boxShadow: `0 0 6px ${brandColors.glow}`,
    animationName: {
      "0%": { opacity: 1 },
      "50%": { opacity: 0.45 },
      "100%": { opacity: 1 },
    },
    animationDuration: "1600ms",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },
  stepLabel: {
    color: inkColors.ink2,
  },
  stepLabelPending: {
    color: inkColors.ink4,
  },
});

export const ApplyProgress: React.FC<ApplyProgressProps> = ({ steps, chipText, header, ariaLabel }) => {
  const styles = useStyles();
  const time = formatTime(Date.now());

  const label = (
    <>
      {`Review agent · ${time}`}
      <span className={styles.intentChip} aria-label={`Intent: ${chipText}`}>
        {chipText}
      </span>
    </>
  );

  return (
    <AsthraBubbleSurface
      label={label}
      className={styles.outer}
      // biome-ignore lint/a11y/useSemanticElements: AsthraBubbleSurface forwards role to its inner div
      bubbleClassName={styles.bubble}
      role="status"
      ariaLabel={ariaLabel}
    >
      <div className={styles.header}>{header}</div>
      {steps.map((step) => (
        <div key={step.id} className={styles.step} data-step-state={step.state}>
          <span
            aria-hidden="true"
            className={mergeClasses(
              styles.stepMarker,
              step.state === "done" && styles.stepMarkerDone,
              step.state === "active" && styles.stepMarkerActive,
            )}
          />
          <span className={mergeClasses(styles.stepLabel, step.state === "pending" && styles.stepLabelPending)}>
            {step.label}
          </span>
        </div>
      ))}
    </AsthraBubbleSurface>
  );
};

export default ApplyProgress;
